// The tick-boxes for sending videos (remembered on this device): shrink larger videos + picture size, just the sound (shows only),
// pick up dropped uploads, keep the screen awake. Inline on the show studio and Video Desk; behind a small "Video options" button elsewhere.
import { SlidersHorizontal } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { useSendPrefs } from "./prefs";

const SIZES = [1080, 720];

const Tick = ({ on, set, label, hint, testid, dark, disabled }) => (
  <label className={`flex items-start gap-2.5 cursor-pointer select-none ${disabled ? "opacity-45 pointer-events-none" : ""}`} data-testid={testid}>
    <input type="checkbox" checked={!!on} onChange={(e) => set(e.target.checked)} disabled={disabled} className={`mt-0.5 h-4 w-4 shrink-0 ${dark ? "accent-[#FFB4A2]" : "accent-[#2E5C55]"}`} data-testid={`${testid}-input`} />
    <span className="min-w-0 text-[13px] font-semibold leading-snug">{label}{hint && <span className={`block text-[11px] font-normal ${dark ? "text-white/50" : "text-[#5C5C5C]"}`}>{hint}</span>}</span>
  </label>
);

export const SendOptions = ({ sound = false, tone = "dark", className = "" }) => {
  const [p, set] = useSendPrefs();
  const dark = tone === "dark";
  const seg = (on) => `px-3 py-1 rounded-full text-xs font-bold transition-colors ${on ? (dark ? "bg-[#FFB4A2] text-[#1A0F0C]" : "bg-[#2E5C55] text-white") : dark ? "text-white/70 hover:bg-white/10" : "text-[#3a3a3a] hover:bg-black/5"}`;
  const audio = sound && p.soundOnly;
  return (
    <div className={`grid gap-3 ${dark ? "text-white/90" : "text-[#1A1A1A]"} ${className}`} data-testid="video-send-options">
      <div className="grid gap-2">
        <Tick on={p.shrink} set={(v) => set({ shrink: v })} disabled={audio} dark={dark} testid="vs-shrink" label="Shrink larger videos"
          hint="Done on this device first — THE WALL's server does it if this device can't" />
        <div className={`ml-6 flex flex-wrap items-center gap-2 ${!p.shrink || audio ? "opacity-45 pointer-events-none" : ""}`} data-testid="vs-size">
          <span className={`text-[11px] font-semibold ${dark ? "text-white/55" : "text-[#5C5C5C]"}`}>Picture size</span>
          <div className={`inline-flex rounded-full p-0.5 ${dark ? "bg-white/10" : "bg-black/5"}`} role="radiogroup" aria-label="Picture size">
            {SIZES.map((s) => <button key={s} type="button" role="radio" aria-checked={p.size === s} onClick={() => set({ size: s })} className={seg(p.size === s)} data-testid={`vs-size-${s}`}>{s}p</button>)}
          </div>
        </div>
      </div>
      {sound && <Tick on={p.soundOnly} set={(v) => set({ soundOnly: v })} dark={dark} testid="vs-sound" label="Send just the sound" hint="Takes the sound out of each video and adds it as a take — the picture isn't sent" />}
      <Tick on={p.resume} set={(v) => set({ resume: v })} dark={dark} testid="vs-resume" label="Pick up where a dropped upload stopped" hint="If the connection drops, it waits and carries on from there" />
      <Tick on={p.awake} set={(v) => set({ awake: v })} dark={dark} testid="vs-awake" label="Keep the screen awake while sending" />
    </div>
  );
};

export const SendOptionsButton = ({ sound = false, tone = "light", className = "" }) => {
  const dark = tone === "dark";
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button type="button" className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-2 text-xs font-bold transition-colors ${dark ? "border-white/30 text-white hover:bg-white/10" : "border-[#E2E0DC] text-[#1A1A1A] bg-white hover:bg-[#F5F5F5]"} ${className}`} data-testid="video-send-options-btn">
          <SlidersHorizontal className="h-3.5 w-3.5" /> Video options
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className={`z-[2000] w-80 rounded-2xl p-4 ${dark ? "bg-[#141019] border-white/15" : "bg-white border-[#E2E0DC]"}`} data-testid="video-send-options-pop">
        <SendOptions sound={sound} tone={tone} />
      </PopoverContent>
    </Popover>
  );
};
